import * as THREE from 'three';
import { FullscreenPass, makeTarget } from '../gpu';
import { HULL_GLSL } from './hullGlsl';
import { WAVE_COUNT, type WaveField } from '../../sim/waves';

// Persistent foam around the ship, simulated in a world-space texture that
// follows the ship (texel-snapped). Every frame the previous state is
// advected into the new frame, decays and diffuses, and new foam is injected
// where crests fold (Gerstner Jacobian), where the hull meets the water, in
// the wake behind the stern and wherever something splashed.
//   R: whitecaps (fast decay)   G: wake foam (slow)   B: aerated water

export const FOAM_RES = 512;
export const FOAM_SIZE = 300;
const MAX_SPLASH = 12;

const FRAG = /* glsl */ `
${HULL_GLSL}
uniform sampler2D uPrev;
uniform vec2 uShift;
uniform vec2 uCenter;
uniform float uDt;
uniform vec4 uWaveA[${WAVE_COUNT}];
uniform vec4 uWaveB[${WAVE_COUNT}];
uniform float uWhitecap;
uniform mat4 uShipInv;
uniform vec3 uShipVel;
uniform float uHeave;
uniform vec4 uSplash[${MAX_SPLASH}];
uniform float uTime;
in vec2 vUv;

float fhash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec2 texel = vec2(1.0 / ${FOAM_RES}.0);
  vec2 puv = vUv + uShift;
  vec4 prev = vec4(0.0);
  if (all(greaterThan(puv, texel)) && all(lessThan(puv, 1.0 - texel))) {
    prev = texture(uPrev, puv) * 0.6
      + (texture(uPrev, puv + vec2(texel.x, 0.0)) + texture(uPrev, puv - vec2(texel.x, 0.0))
      + texture(uPrev, puv + vec2(0.0, texel.y)) + texture(uPrev, puv - vec2(0.0, texel.y))) * 0.1;
  }
  prev.r *= exp(-uDt / 3.2);
  prev.g *= exp(-uDt / 26.0);
  prev.b *= exp(-uDt / 7.0);

  // Surface at this Lagrangian point.
  vec2 p0 = (vUv - 0.5) * ${FOAM_SIZE}.0;
  vec3 d = vec3(0.0);
  float jxx = 0.0, jzz = 0.0, jxz = 0.0;
  for (int i = 0; i < ${WAVE_COUNT}; i++) {
    vec4 a = uWaveA[i];
    vec4 b = uWaveB[i];
    float th = a.z * dot(a.xy, p0) + b.x;
    float s = sin(th), c = cos(th);
    d.y += a.w * c;
    d.xz -= b.y * a.xy * s;
    float qk = b.y * a.z * c;
    jxx -= qk * a.x * a.x;
    jzz -= qk * a.y * a.y;
    jxz -= qk * a.x * a.y;
  }
  float J = (1.0 + jxx) * (1.0 + jzz) - jxz * jxz;
  float wc = (1.0 - smoothstep(uWhitecap - 0.3, uWhitecap, J));
  prev.r = max(prev.r, wc);
  prev.b = max(prev.b, wc * 0.6);

  // Hull contact and wake.
  vec3 wp = vec3(uCenter.x + p0.x + d.x, d.y, uCenter.y + p0.y + d.z);
  vec3 sl = (uShipInv * vec4(wp, 1.0)).xyz;
  float speed = length(uShipVel.xz);
  if (sl.x > -40.0 && sl.x < 26.0 && abs(sl.z) < 16.0) {
    float hb = hHalfBreadthXY(sl.x, sl.y);
    float hw = hHalfBreadthXY(sl.x, 0.0);
    float hbe = max(hb, hw);
    float dist = abs(sl.z) - hbe;
    float band = (1.0 - smoothstep(0.0, 1.6, dist)) * step(-2.5, dist);
    float n = fhash(floor(p0 * 2.3) + floor(uTime * 6.0));
    float stir = clamp(speed * 0.22 + abs(uHeave) * 0.35 + 0.08, 0.0, 1.4);
    float bow = smoothstep(8.0, 17.5, sl.x);
    float amount = band * stir * (0.35 + 0.65 * bow) * (0.6 + 0.4 * n);
    prev.g = max(prev.g, amount * 0.9);
    prev.b = max(prev.b, amount);
    // Turbulent wake off the counter and rudder.
    float stern = (1.0 - smoothstep(-19.0, -15.5, sl.x)) * smoothstep(-40.0, -19.0, sl.x);
    float wake = stern * (1.0 - smoothstep(0.5, 3.6 + (-19.0 - sl.x) * 0.12, abs(sl.z))) * clamp(speed * 0.25, 0.0, 1.0);
    prev.g = max(prev.g, wake * (0.55 + 0.45 * n));
    prev.b = max(prev.b, wake);
  }

  for (int i = 0; i < ${MAX_SPLASH}; i++) {
    vec4 s = uSplash[i];
    if (s.w <= 0.0) continue;
    float r = length(p0 + uCenter - s.xy);
    float k = (1.0 - smoothstep(s.z * 0.3, s.z, r)) * s.w;
    prev.g = max(prev.g, k);
    prev.b = max(prev.b, k);
  }
  gl_FragColor = clamp(prev, 0.0, 1.0);
}
`;

export class FoamSim {
  private readonly rt = [0, 1].map(() =>
    makeTarget(FOAM_RES, FOAM_RES, { wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping }),
  );
  private index = 0;
  private readonly pass: FullscreenPass;
  /** World-space centre of the foam texture (texel snapped). */
  readonly center = new THREE.Vector2();
  private initialised = false;
  private readonly waveA = new Float32Array(WAVE_COUNT * 4);
  private readonly waveB = new Float32Array(WAVE_COUNT * 4);
  private readonly aVec: THREE.Vector4[] = [];
  private readonly bVec: THREE.Vector4[] = [];
  private readonly splashes: THREE.Vector4[] = [];
  private pending: { x: number; z: number; r: number; a: number }[] = [];

  constructor() {
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.aVec.push(new THREE.Vector4());
      this.bVec.push(new THREE.Vector4());
    }
    for (let i = 0; i < MAX_SPLASH; i++) this.splashes.push(new THREE.Vector4());
    this.pass = new FullscreenPass(FRAG, {
      uPrev: { value: null },
      uShift: { value: new THREE.Vector2() },
      uCenter: { value: this.center },
      uDt: { value: 0 },
      uWaveA: { value: this.aVec },
      uWaveB: { value: this.bVec },
      uWhitecap: { value: 0.3 },
      uShipInv: { value: new THREE.Matrix4() },
      uShipVel: { value: new THREE.Vector3() },
      uHeave: { value: 0 },
      uSplash: { value: this.splashes },
      uTime: { value: 0 },
    });
  }

  get texture(): THREE.Texture {
    return this.rt[this.index]!.texture;
  }

  splash(x: number, z: number, radius: number, amount: number): void {
    this.pending.push({ x, z, r: radius, a: amount });
  }

  update(renderer: THREE.WebGLRenderer, dt: number, time: number, waves: WaveField, windSpeed: number, shipX: number, shipZ: number, shipInv: THREE.Matrix4, shipVelLocal: THREE.Vector3, heave: number): void {
    const texel = FOAM_SIZE / FOAM_RES;
    const cx = Math.round(shipX / texel) * texel;
    const cz = Math.round(shipZ / texel) * texel;
    const u = this.pass.uniforms;
    if (!this.initialised) {
      this.center.set(cx, cz);
      this.initialised = true;
    }
    u.uShift!.value.set((cx - this.center.x) / FOAM_SIZE, (cz - this.center.y) / FOAM_SIZE);
    this.center.set(cx, cz);
    waves.packUniforms(cx, cz, this.waveA, this.waveB);
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.aVec[i]!.fromArray(this.waveA, i * 4);
      this.bVec[i]!.fromArray(this.waveB, i * 4);
    }
    // Whitecaps start around 5 m/s of wind and grow with it.
    u.uWhitecap!.value = whitecapThreshold(windSpeed);
    u.uDt!.value = dt;
    u.uTime!.value = time;
    u.uShipInv!.value.copy(shipInv);
    u.uShipVel!.value.copy(shipVelLocal);
    u.uHeave!.value = heave;
    for (let i = 0; i < MAX_SPLASH; i++) {
      const s = this.pending[i];
      if (s) this.splashes[i]!.set(s.x, s.z, s.r, s.a);
      else this.splashes[i]!.set(0, 0, 0, 0);
    }
    this.pending = this.pending.slice(MAX_SPLASH);
    const src = this.rt[this.index]!;
    this.index ^= 1;
    u.uPrev!.value = src.texture;
    this.pass.render(renderer, this.rt[this.index]!);
  }
}

/**
 * Jacobian below which crests break. Beaufort 3 shows scattered whitecaps,
 * a gale streaks the whole sea white.
 */
export function whitecapThreshold(windSpeed: number): number {
  return THREE.MathUtils.clamp(0.3 + (windSpeed - 5.5) * 0.036, 0.15, 0.9);
}
